import { useEffect, useState, useRef } from 'react'
import { saveUserPrediction } from '../api/client'
import { useUIStore } from '../store/uiStore'
import './UserPredictionModal.css'

export default function UserPredictionModal({
  questionId,
  onSaved,
}: {
  questionId: string | null | undefined
  onSaved: () => void
}) {
  const isOpen = useUIStore((s) => s.modals.userPrediction)
  const closeModal = useUIStore((s) => s.closeModal)

  const [userName, setUserName] = useState('')
  const [probability, setProbability] = useState(50)
  const [confidenceLow, setConfidenceLow] = useState(40)
  const [confidenceHigh, setConfidenceHigh] = useState(60)
  const [rationale, setRationale] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const trackRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState<'point' | 'low' | 'high' | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setIsSaving(false)
  }, [isOpen])

  if (!isOpen) return null

  const handleClose = () => {
    closeModal('userPrediction')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!questionId) {
      alert('No question selected.')
      return
    }
    if (!userName.trim()) {
      alert('Please enter your name')
      return
    }
    if (confidenceLow >= confidenceHigh) {
      alert('Confidence interval lower bound must be less than upper bound.')
      return
    }
    if (probability < confidenceLow || probability > confidenceHigh) {
      alert('Your prediction should fall within your confidence interval.')
      return
    }

    setIsSaving(true)
    try {
      await saveUserPrediction({
        question_id: questionId,
        user_name: userName.trim(),
        probability: probability / 100,
        confidence_low: confidenceLow / 100,
        confidence_high: confidenceHigh / 100,
        rationale: rationale.trim(),
      })
      closeModal('userPrediction')
      onSaved()
      setUserName('')
      setProbability(50)
      setConfidenceLow(40)
      setConfidenceHigh(60)
      setRationale('')
    } catch (error) {
      console.error('Failed to save prediction:', error)
      alert('Failed to save prediction: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsSaving(false)
    }
  }

  // Handle dragging on the visual scale
  const getValueFromPosition = (clientX: number): number => {
    if (!trackRef.current) return 50
    const rect = trackRef.current.getBoundingClientRect()
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
    return Math.round((x / rect.width) * 100)
  }

  const handleMouseDown = (e: React.MouseEvent, type: 'point' | 'low' | 'high') => {
    e.preventDefault()
    setIsDragging(type)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return
    const value = getValueFromPosition(e.clientX)
    
    if (isDragging === 'point') {
      setProbability(Math.max(confidenceLow, Math.min(confidenceHigh, value)))
    } else if (isDragging === 'low') {
      setConfidenceLow(Math.max(0, Math.min(probability - 1, value)))
    } else if (isDragging === 'high') {
      setConfidenceHigh(Math.max(probability + 1, Math.min(100, value)))
    }
  }

  const handleMouseUp = () => {
    setIsDragging(null)
  }

  // Touch support
  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !e.touches[0]) return
    const value = getValueFromPosition(e.touches[0].clientX)
    
    if (isDragging === 'point') {
      setProbability(Math.max(confidenceLow, Math.min(confidenceHigh, value)))
    } else if (isDragging === 'low') {
      setConfidenceLow(Math.max(0, Math.min(probability - 1, value)))
    } else if (isDragging === 'high') {
      setConfidenceHigh(Math.max(probability + 1, Math.min(100, value)))
    }
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('touchmove', handleTouchMove)
      window.addEventListener('touchend', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', handleMouseUp)
      }
    }
  }, [isDragging, confidenceLow, confidenceHigh, probability])

  const rangeWidth = confidenceHigh - confidenceLow

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content user-prediction-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header">
          <h2>Add your own prediction</h2>
          <button className="modal-close" type="button" onClick={handleClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <form className="up-form" onSubmit={handleSubmit}>
            <div className="up-field">
              <label className="up-label">Your name</label>
              <input 
                className="up-input"
                value={userName} 
                onChange={(e) => setUserName(e.target.value)} 
                placeholder="Enter your name" 
              />
            </div>

            {/* Interactive visual representation */}
            <div className="up-visual-container">
              <div className="up-visual-header">
                <div className="up-visual-value-group">
                  <span className="up-visual-value-label">Your prediction</span>
                  <span className="up-visual-value-number">{probability}%</span>
                </div>
                <div className="up-visual-value-group">
                  <span className="up-visual-value-label">90% confidence interval</span>
                  <span className="up-visual-value-number">{confidenceLow}% – {confidenceHigh}%</span>
                </div>
              </div>

              <div className="up-visual-help">
                Drag the handles to adjust your prediction and confidence interval
              </div>

              <div className="up-visual-scale">
                <div className="up-visual-track" ref={trackRef}>
                  {/* Confidence interval band */}
                  <div 
                    className="up-visual-ci-band"
                    style={{
                      left: `${confidenceLow}%`,
                      width: `${rangeWidth}%`
                    }}
                  />
                  
                  {/* Draggable lower bound handle */}
                  <div 
                    className={`up-visual-handle up-visual-handle-low ${isDragging === 'low' ? 'dragging' : ''}`}
                    style={{ left: `${confidenceLow}%` }}
                    onMouseDown={(e) => handleMouseDown(e, 'low')}
                    onTouchStart={(e) => {
                      e.preventDefault()
                      setIsDragging('low')
                    }}
                  >
                    <span className="up-visual-handle-label">{confidenceLow}%</span>
                  </div>
                  
                  {/* Draggable upper bound handle */}
                  <div 
                    className={`up-visual-handle up-visual-handle-high ${isDragging === 'high' ? 'dragging' : ''}`}
                    style={{ left: `${confidenceHigh}%` }}
                    onMouseDown={(e) => handleMouseDown(e, 'high')}
                    onTouchStart={(e) => {
                      e.preventDefault()
                      setIsDragging('high')
                    }}
                  >
                    <span className="up-visual-handle-label">{confidenceHigh}%</span>
                  </div>
                  
                  {/* Draggable point estimate */}
                  <div 
                    className={`up-visual-point ${isDragging === 'point' ? 'dragging' : ''}`}
                    style={{ left: `${probability}%` }}
                    onMouseDown={(e) => handleMouseDown(e, 'point')}
                    onTouchStart={(e) => {
                      e.preventDefault()
                      setIsDragging('point')
                    }}
                  >
                    <span className="up-visual-point-label">{probability}%</span>
                  </div>
                </div>
                
                <div className="up-visual-labels">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            <div className="up-field">
              <label className="up-label">Rationale (optional)</label>
              <textarea
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder="Explain your reasoning…"
                rows={4}
                className="up-textarea"
              />
            </div>

            <div className="up-actions">
              <button
                type="button"
                className="rounded-xl border border-rule bg-paper shadow-delphi px-4 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-xl px-4 py-2 text-sm font-semibold bg-green text-white shadow-delphi hover:brightness-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
